import React, { useEffect, useRef } from "react";
import { AiOutlineLoading } from "react-icons/ai";

export const DeleteModal = ({ showModal, setShowModal, name, onDelete, loading }) => {
    const cancelRef = useRef(null);

    // Close on Esc + lock scroll while modal is open + focus the Cancel button
    useEffect(() => {
        if (!showModal) return;
        const onKey = (e) => e.key === "Escape" && setShowModal(false);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKey);
        // focus default action
        setTimeout(() => cancelRef.current?.focus(), 0);
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [showModal, setShowModal]);

    if (!showModal) return null;

    return (
        <div
            className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-title"
            onClick={() => setShowModal(false)} // backdrop click to close
        >
            <div
                className="w-full max-w-md rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200 overflow-hidden transform transition-all"
                onClick={(e) => e.stopPropagation()} // prevent close when clicking inside
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <h2 id="delete-title" className="text-sm font-semibold text-gray-900">
                        Confirm deletion
                    </h2>
                    <button
                        type="button"
                        onClick={() => setShowModal(false)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-900 text-white hover:bg-gray-800 transition"
                        aria-label="Close"
                    >
                        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5">
                    <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-50 flex items-center justify-center">
                        <svg
                            className="h-6 w-6 text-red-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v4m0 4h.01M4.93 4.93l14.14 14.14M12 2a10 10 0 100 20 10 10 0 000-20z" />
                        </svg>
                    </div>

                    <p className="text-sm text-gray-700 text-center">
                        Are you sure you want to permanently delete
                        <br />
                        <span className="mt-1 inline-block font-medium text-gray-900">
                            {name || "this item"}?
                        </span>
                    </p>
                </div>

                {/* Footer */}
                <div className="px-6 pb-6">
                    <div className="flex gap-2">
                        <button
                            type="button"
                            disabled={loading}
                            onClick={onDelete}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2.5 disabled:opacity-60"
                        >
                            {loading ? (
                                <>
                                    <AiOutlineLoading className="animate-spin" />
                                    Deleting…
                                </>
                            ) : (
                                "Yes, delete"
                            )}
                        </button>
                        <button
                            type="button"
                            ref={cancelRef}
                            onClick={() => setShowModal(false)}
                            className="inline-flex w-full items-center justify-center rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-800 text-sm font-medium px-4 py-2.5"
                        >
                            Cancel
                        </button>
                    </div>
                    {/* Hint */}
                    <p className="mt-3 text-[11px] text-gray-500 text-center">
                        This action cannot be undone.
                    </p>
                </div>
            </div>
        </div>
    );
};
